import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  getList,
  deleteList,
  addItem,
  deleteItem,
  updateItem,
  reorderItems,
  exportMarkdown,
  ListData,
  ListItem,
} from '../repository/listRepository';
import { ItemRow } from './ItemRow';
import { AddItemForm } from './AddItemForm';

export const ListView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [list, setList] = useState<ListData | null>(null);
  const [loading, setLoading] = useState(true);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const fetchList = useCallback(() => {
    if (!id) return;
    getList(id).then(setList).finally(() => setLoading(false));
  }, [id]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const handleDelete = async () => {
    if (!id || !window.confirm('Delete this list and all its items?')) return;
    await deleteList(id);
    navigate('/');
  };

  const handleAddItem = async (title: string, isHonorableMention: boolean) => {
    if (!id) return;
    await addItem(id, title, '', 0, isHonorableMention);
    fetchList();
  };

  const handleDeleteItem = async (itemId: string) => {
    if (!id) return;
    await deleteItem(id, itemId);
    fetchList();
  };

  const handleUpdateItem = async (itemId: string, updates: Partial<ListItem>) => {
    if (!id) return;
    await updateItem(id, itemId, updates);
    fetchList();
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    if (!id || !list?.items) return;
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const items = [...list.items];
    const oldIndex = items.findIndex(i => i.uuid === active.id);
    const newIndex = items.findIndex(i => i.uuid === over.id);
    const [moved] = items.splice(oldIndex, 1);
    items.splice(newIndex, 0, moved);

    setList({ ...list, items });
    await reorderItems(id, items.map(i => i.uuid));
    fetchList();
  };

  const handleCopyMarkdown = async () => {
    if (!id) return;
    const md = await exportMarkdown(id);
    await navigator.clipboard.writeText(md);
  };

  if (loading) return <div className="loading">Loading...</div>;
  if (!list) return <div className="empty-state"><p>List not found.</p></div>;

  const isRanked = list.list_type === 'ranked';
  const items = list.items || [];
  const honorable = list.honorable_mentions || [];

  return (
    <div>
      <div className="list-header">
        <h1 className="list-name">{list.name}</h1>
        {list.notes && <p className="list-notes">{list.notes}</p>}
        <div className="list-actions">
          <Link to={`/lists/${id}/edit`} className="btn btn-sm">Edit</Link>
          <Link to={`/lists/${id}/history`} className="btn btn-sm">History</Link>
          <button onClick={handleCopyMarkdown} className="btn btn-sm">Copy Markdown</button>
          <button onClick={handleDelete} className="btn btn-sm btn-danger">Delete</button>
        </div>
      </div>

      <div className="items-section">
        {isRanked ? (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={items.map(i => i.uuid)} strategy={verticalListSortingStrategy}>
              {items.map((item, index) => (
                <ItemRow
                  key={item.uuid}
                  item={item}
                  rank={index + 1}
                  showDragHandle={true}
                  onDelete={() => handleDeleteItem(item.uuid)}
                  onUpdate={(updates) => handleUpdateItem(item.uuid, updates)}
                />
              ))}
            </SortableContext>
          </DndContext>
        ) : (
          items.map(item => (
            <ItemRow
              key={item.uuid}
              item={item}
              onDelete={() => handleDeleteItem(item.uuid)}
              onUpdate={(updates) => handleUpdateItem(item.uuid, updates)}
            />
          ))
        )}

        <AddItemForm onAdd={(title) => handleAddItem(title, false)} placeholder="Add item..." />
      </div>

      {isRanked && (
        <div className="honorable-section">
          <h3 className="honorable-title">Honorable Mentions</h3>
          {honorable.map(item => (
            <ItemRow
              key={item.uuid}
              item={item}
              onDelete={() => handleDeleteItem(item.uuid)}
              onUpdate={(updates) => handleUpdateItem(item.uuid, updates)}
            />
          ))}
          <AddItemForm onAdd={(title) => handleAddItem(title, true)} placeholder="Add honorable mention..." />
        </div>
      )}
    </div>
  );
};
